import { vsingleton } from '@niknevis/vue-modules'
import { Inertia, Page, VisitOptions } from '@niknevis/inertia'
import { CancelTokenSource } from 'axios'

type InertiaHooks = {
  onBeforeAsync?: () => Promise<boolean>,
  onBefore: () => void | boolean,
  onCancelToken: (cancelToken: CancelTokenSource) => void
  onStart: (visit: VisitOptions & { url: string }) => void | boolean
  onProgress: (progress: ProgressEvent) => void
  onFinish: () => void
  onCancel: () => void
  onSuccess: (page: Page) => void | Promise<any>
  onError: (errors: Record<string, string>) => void
}

class InertiaPromise extends Promise<void> {
  private readonly hooks: InertiaHooks = {
    onBefore: () => {
    },
    onCancel: () => {
    },
    onCancelToken: () => {
    },
    onError: () => {
    },
    onFinish: () => {
    },
    onProgress: () => {
    },
    onStart: () => {
    },
    onSuccess: () => {
    }
  }

  constructor(
    executor: (hooks: InertiaHooks) => Promise<any>
  ) {
    super((resolve) => {
      setTimeout(async () => {
        if (this.hooks.onBeforeAsync) {
          const go = await this.hooks.onBeforeAsync()
          if (!go) return
        }
        resolve(executor(this.hooks))
      }, 0)
    })
  }

  static get [Symbol.species]() {
    return Promise
  }

  private setHook(type: keyof InertiaHooks, fn) {
    this.hooks[type] = fn
  }

  onBeforeAsync(callback: InertiaHooks['onBeforeAsync']) {
    this.setHook('onBeforeAsync', callback)
    return this
  }

  onBefore(callback: InertiaHooks['onBefore']) {
    this.setHook('onBefore', callback)
    return this
  }

  onCancelToken(callback: InertiaHooks['onCancelToken']) {
    this.setHook('onCancelToken', callback)
    return this
  }

  onStart(callback: InertiaHooks['onStart']) {
    this.setHook('onStart', callback)
    return this
  }

  onProgress(callback: InertiaHooks['onProgress']) {
    this.setHook('onProgress', callback)
    return this
  }

  onFinish(callback: InertiaHooks['onFinish']) {
    this.setHook('onFinish', callback)
    return this
  }

  onCancel(callback: InertiaHooks['onCancel']) {
    this.setHook('onCancel', callback)
    return this
  }

  onSuccess(callback: InertiaHooks['onSuccess']) {
    this.setHook('onSuccess', callback)
    return this
  }

  onError(callback: InertiaHooks['onError']) {
    this.setHook('onError', callback)
    return this
  }
}

@vsingleton()
export default class InertiaService {
  constructor() {
  }

  visit(
    url: string,
    options?: VisitOptions & { data?: object }
  ): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.visit(url, {
        ...options,
        ...hooks
      }))
  }

  get(url: string, data?: object, options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.get(url, data, {
        ...options,
        ...hooks
      }))
  }

  patch(url: string, data?: object, options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.patch(url, data, {
        ...options,
        ...hooks
      }))
  }

  post(url: string, data?: object, options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.post(url, data, {
        ...options,
        ...hooks
      }))
  }

  put(url: string, data?: object, options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.put(url, data, {
        ...options,
        ...hooks
      }))
  }

  delete(url: string, options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.delete(url, {
        ...options,
        ...hooks
      }))
  }

  reload(options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.reload({
        ...options,
        ...hooks
      }))
  }

  replace(url: string, options?: VisitOptions): InertiaPromise {
    return new InertiaPromise(hooks =>
      Inertia.replace(url, {
        ...options,
        ...hooks
      }))
  }
}
